<template>
    <div class="packlist-items" :class="{ 'packlist-items-vertical': isVertical }" ref="itemsContainer">
      <div 
        v-for="(item, index) in validOpenItems" 
        :key="item.id || `open-${index}`" 
        class="list-item"
        :class="{
          'current-item': item.orderId === currentOrderId,
        }"
        @click="handleItemClick(item)"
        :ref="item.orderId === currentOrderId ? 'currentItem' : undefined"
      >
        <span class="item-index">{{ index + 1 }}</span>
        <img :src="primeLogo" class="prime-logo" alt="Prime Logo" v-if="item.isPrime">
      </div>  
      <div 
        v-for="(item, index) in validPausedItems" 
        :key="item.id || `paused-${index}`" 
        class="list-item warning"
        :class="{
          'current-item': item.orderId === currentOrderId
        }"
        @click="handleItemClick(item)"
        :ref="item.orderId === currentOrderId ? 'currentItem' : undefined"
      >
        <span class="item-index">{{ validOpenItems.length + index + 1 }}</span>
      </div>
      <div 
        v-for="(item, index) in validPackedItems" 
        :key="item.id || `packed-${index}`" 
        class="list-item success"
        :class="{
          'current-item': item.orderId === currentOrderId
        }"
        @click="handleItemClick(item)"
        :ref="item.orderId === currentOrderId ? 'currentItem' : undefined"
      >
        <span class="item-index">{{ validOpenItems.length + validPausedItems.length + index + 1 }}</span>
        <v-icon>mdi-check-circle</v-icon>
      </div>  
    </div>
  </template>
  
  <script>
  import primeLogo from '@/assets/prime.png';
  
  export default {
    name: 'PacklistItems',
    props: {
      openItems: {
        type: Array,
        default: () => []
      },
      pausedItems: {
        type: Array,
        default: () => []
      },
      packedItems: {
        type: Array,
        default: () => []
      },
      currentOrderId: {
        type: String,
        default: null
      },
      isVertical: {
        type: Boolean,
        default: false
      }
    },
    data() {
      return {
        primeLogo
      };
    },
    computed: {
      validOpenItems() {
        if (!Array.isArray(this.openItems)) return [];
        return this.openItems.filter(item => 
          item && 
          typeof item === 'object' && 
          item.id && 
          item.orderId
        );
      },
      validPausedItems() {
        if (!Array.isArray(this.pausedItems)) return [];
        return this.pausedItems.filter(item => 
          item && 
          typeof item === 'object' && 
          item.id && 
          item.orderId
        );
      },
      validPackedItems() {
        if (!Array.isArray(this.packedItems)) return [];
        return this.packedItems.filter(item => 
          item && 
          typeof item === 'object' && 
          item.id && 
          item.orderId
        );
      }
    },
    methods: {
      handleItemClick(item) {
        // Don't allow clicking on the currently selected item
        if (item.orderId === this.currentOrderId) {
          return;
        }
        this.$emit('item-click', item);
      },
      scrollToCurrentItem() {
        this.$nextTick(() => {
          if (this.$refs.currentItem && this.$refs.currentItem[0]) {
            setTimeout(() => {
              if (this.isVertical) {
                // Vertical scrolling - scroll block (vertical) to center
                this.$refs.currentItem[0].scrollIntoView({ 
                  behavior: 'smooth', 
                  block: 'center', 
                  inline: 'nearest'
                });
              } else {
                // Horizontal scrolling - scroll inline (horizontal) to center
                this.$refs.currentItem[0].scrollIntoView({ 
                  behavior: 'smooth', 
                  block: 'nearest', 
                  inline: 'center'
                });
              }
            }, 100);
          }
        });
      }
    },
    watch: {
      currentOrderId: {
        handler() {
          this.scrollToCurrentItem();
        },
        immediate: true
      },
      validOpenItems: {
        handler() {
          if (this.currentOrderId) {
            this.scrollToCurrentItem();
          }
        },
        deep: true
      }
    },
    mounted() {
      this.scrollToCurrentItem();
    }
  }
  </script>
  
  <style scoped>
  /* Default horizontal layout */
  .packlist-items {
    display: flex;
    overflow-x: auto;
    overflow-y: hidden;
    scrollbar-width: none;
    -ms-overflow-style: none;
  }
  
  .packlist-items::-webkit-scrollbar {
    display: none;
  }
  
  /* Vertical layout for desktop sidebar */
  .packlist-items-vertical {
    flex-direction: column;
    overflow-x: hidden;
    overflow-y: auto;
    height: 100%;
    width: 100%;
  }
  
  /* Default list item */
  .list-item {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 80px;
    height: 40px;
    min-width: 80px;
    flex-shrink: 0;
    background-color: #e0e0e0;
    cursor: pointer;
    transition: all 0.2s ease;
    font-weight: 600;
    box-shadow: inset -4px 0 5px -2px rgba(0, 0, 0, 0.2); /* Inner shadow on right for tablet */
  }
  
  /* Vertical layout - inner shadow on bottom for desktop */
  .packlist-items-vertical .list-item {
    width: 80px;
    height: 40px;
    align-self: flex-start; /* Align to left instead of center */
    box-shadow: inset 0 -4px 5px -2px rgba(0, 0, 0, 0.2); /* Inner shadow on bottom for desktop */
  }
  
  /* Status colors */
  .list-item.warning {
    background-color: #ff9800;
    color: white;
  }
  
  .list-item.success {
    background-color: #4caf50;
    color: white;
  }
  
  .prime-logo {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 80%;
    z-index: 5;
  }
  
  /* Index number positioning */
  .item-index {
    position: absolute;
    top: 2px;
    left: 2px;
    background-color: rgba(0, 0, 0, 0.7);
    color: white;
    font-size: 10px;
    font-weight: bold;
    padding: 1px 4px;
    border-radius: 2px;
    line-height: 1;
    z-index: 10;
  }
  
  /* Simple blue background for the current item */
  .current-item {
    background-color: #2196F3 !important; /* Blue background with !important to override other colors */
    color: white !important; /* White text for better contrast */
    cursor: default !important; /* Not clickable cursor */
    pointer-events: none; /* Disable all pointer events */
  }
  
  /* Make sure this has highest specificity */
  .success.current-item {
    background-color: #2196F3 !important;
    color: white !important;
    cursor: default !important; /* Not clickable cursor */
    pointer-events: none; /* Disable all pointer events */
  }
  
  /* Warning current item styling */
  .warning.current-item {
    background-color: #2196F3 !important;
    color: white !important;
    cursor: default !important; /* Not clickable cursor */
    pointer-events: none; /* Disable all pointer events */
  }

  /* And ensure the check icon also changes color */
  .success.current-item .v-icon {
    color: white !important;
  }
  
  /* Center the check icon properly */
  .list-item .v-icon {
    position: relative;
    z-index: 5;
  }
  </style>